import React, { useState, useCallback, useRef } from 'react';
import { motion, Variants } from 'framer-motion';
import ImageUpIcon from './icons/ImageUpIcon';
import SparklesIcon from './icons/SparklesIcon';
import Wand2Icon from './icons/Wand2Icon';
import CheckCircleIcon from './icons/CheckCircleIcon';
import CameraIcon from './icons/CameraIcon';
import CameraCapture from './CameraCapture';
import OnboardingModal from './OnboardingModal';
import { compressImage, shouldCompress, formatFileSize, getCompressionRatio } from '../utils/imageCompression';
import {
  isConversionNeeded,
  convertToSupportedFormat,
  getAcceptString,
  getFormatDisplayName
} from '../utils/imageFormatConverter';
import UploaderBackground from './UploaderBackground';


interface ImageUploaderProps {
  onImageUpload: (file: File) => void;
}

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({ icon, title, description }) => (
  <div className="bg-white/40 dark:bg-zinc-900/40 p-3 sm:p-4 md:p-5 lg:p-6 rounded-xl md:rounded-2xl border border-white/50 dark:border-zinc-800/80 text-center flex flex-col items-center backdrop-blur-md transition-all hover:shadow-2xl hover:shadow-zinc-300/20 dark:hover:shadow-black/40 hover:-translate-y-1">
    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-white/80 dark:bg-zinc-800/80 flex items-center justify-center border border-zinc-200 dark:border-zinc-700 text-teal-500 dark:text-teal-400">
      {icon}
    </div>
    <h3 className="font-bold text-sm sm:text-base md:text-lg mt-2 sm:mt-3 md:mt-4 text-zinc-800 dark:text-zinc-100">{title}</h3>
    <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400 mt-1">{description}</p>
  </div>
);


const ImageUploader: React.FC<ImageUploaderProps> = ({ onImageUpload }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionMessage, setConversionMessage] = useState<string | null>(null);
  const dropzoneRef = useRef<HTMLLabelElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLLabelElement>) => {
    if (!dropzoneRef.current) return;
    const rect = dropzoneRef.current.getBoundingClientRect();
    setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const processAndUploadFile = async (file: File) => {
    try {
      setIsCompressing(true);
      setConversionMessage(null);

      let processedFile = file;

      // Check if format conversion is needed (HEIC, BMP, TIFF, GIF, AVIF, SVG)
      if (isConversionNeeded(file)) {
        setIsConverting(true);
        const formatName = getFormatDisplayName(file.type || 'unknown');
        setConversionMessage(`Converting ${formatName} to compatible format...`);

        try {
          const result = await convertToSupportedFormat(file);
          processedFile = result.file;

          if (result.wasConverted) {
            setConversionMessage(`Converted from ${formatName} successfully!`);
          }
        } catch (conversionError) {
          console.error('Image format conversion failed:', conversionError);
          setConversionMessage('Format conversion failed. Please use JPEG, PNG, or WebP.');
          setIsConverting(false);
          setIsCompressing(false);
          return;
        }
      }

      // NO COMPRESSION - use original high-quality images
      // Dental imagery requires maximum quality for accurate AI analysis
      onImageUpload(processedFile);
    } catch (error) {
      console.error('Image upload error:', error);
      onImageUpload(file);
    } finally {
      setIsCompressing(false);
      setIsConverting(false);
      // Clear conversion message after a short delay
      setTimeout(() => setConversionMessage(null), 3000);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processAndUploadFile(e.target.files[0]);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processAndUploadFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleCapture = (file: File) => {
    processAndUploadFile(file);
    setIsCameraOpen(false);
  };

  if (isCameraOpen) {
    return <CameraCapture onCapture={handleCapture} onCancel={() => setIsCameraOpen(false)} />;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 15 },
    },
  };

  return (
    <>
      {/* Onboarding Modal - Shows only for first-time users */}
      <OnboardingModal onComplete={() => { }} />

      <UploaderBackground>
        <div className="w-full h-full flex flex-col items-center justify-center p-2 sm:p-4 md:p-6 lg:p-8 text-center overflow-hidden min-h-0">
          <motion.div
            className="w-full max-w-5xl flex flex-col items-center min-h-0"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h2 variants={itemVariants} className="text-xs sm:text-sm font-semibold uppercase tracking-widest text-teal-600 dark:text-teal-400">Getting Started</motion.h2>
            <motion.h1 variants={itemVariants} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-zinc-900 to-zinc-600 dark:from-zinc-100 dark:to-zinc-400 py-1 sm:py-2 mt-0 sm:mt-1">FuturaSmile</motion.h1>
            <motion.p variants={itemVariants} className="text-sm sm:text-base md:text-lg text-zinc-600 dark:text-zinc-400 mt-2 sm:mt-3 md:mt-4 max-w-2xl px-2">The future of smile design is here. Upload a patient's photo to generate a photorealistic preview of their new smile in seconds.</motion.p>

            <motion.div variants={itemVariants} className="w-full max-w-3xl mt-4 sm:mt-6 md:mt-8 lg:mt-10">
              <label
                ref={dropzoneRef}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onMouseMove={handleMouseMove}
                className={`group relative flex flex-col items-center justify-center w-full aspect-[16/10] md:aspect-[16/9] lg:aspect-[16/7] rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 text-center cursor-pointer transition-all duration-300 ease-in-out bg-zinc-100/50 dark:bg-zinc-900/40 border-2 border-dashed border-zinc-300/70 dark:border-zinc-800/70 overflow-hidden ${isDragging ? 'border-solid border-teal-500/80 bg-teal-500/10 scale-[1.02] shadow-2xl shadow-teal-500/20' : 'hover:border-teal-500/50'}`}
                aria-label="File upload area"
              >
                <div
                  className="pointer-events-none absolute -inset-px rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(20, 184, 166, 0.1), transparent 40%)`,
                  }}
                />
                <div className={`absolute inset-0 transition-all duration-300 ${isDragging ? 'bg-teal-500/10' : ''}`} />

                <input
                  type="file"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/png, image/jpeg, image/webp, image/heic, image/heif, image/bmp, image/tiff, image/gif, image/avif, image/svg+xml, .heic, .heif, .bmp, .tiff, .tif, .gif, .avif, .svg"
                  tabIndex={-1}
                  aria-label="Upload image file"
                />
                <div className="z-10 flex flex-col items-center justify-center space-y-2 sm:space-y-3 md:space-y-4 text-zinc-500 dark:text-zinc-400 pointer-events-none transform transition-transform duration-300 group-hover:scale-105">
                  <div className={`transition-all duration-300 ease-out p-2 sm:p-3 md:p-4 rounded-lg md:rounded-xl bg-white/80 dark:bg-zinc-800/50 border border-zinc-300/80 dark:border-zinc-700/80 ${isDragging ? 'scale-110 border-teal-500/30 bg-teal-100/80 dark:bg-teal-950/80' : 'group-hover:bg-teal-100/50 dark:group-hover:bg-teal-500/10'}`}>
                    <ImageUpIcon className={`w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 transition-colors ${isDragging ? 'text-teal-600 dark:text-teal-400' : 'text-zinc-500 dark:text-zinc-400 group-hover:text-teal-600 dark:group-hover:text-teal-400'}`} />
                  </div>
                  <p className="font-medium text-sm sm:text-base md:text-lg text-zinc-600 dark:text-zinc-300 px-2">
                    Drag photo here or <span className="font-semibold text-teal-600 dark:text-teal-400">click to browse</span>
                  </p>
                  <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-500 px-2">Use a clear, front-facing photo for best results</p>
                  {(isConverting || conversionMessage) && (
                    <p className={`text-xs sm:text-sm mt-2 px-2 font-medium ${conversionMessage?.includes('failed')
                      ? 'text-red-500'
                      : conversionMessage?.includes('successfully')
                        ? 'text-green-500 dark:text-green-400'
                        : 'text-teal-500 dark:text-teal-400'
                      }`}>
                      {isConverting && !conversionMessage?.includes('successfully') && (
                        <span className="inline-block animate-spin mr-1">⟳</span>
                      )}
                      {conversionMessage || 'Converting image format...'}
                    </p>
                  )}
                </div>
              </label>

              <div className="my-3 sm:my-4 md:my-5 lg:my-6 flex items-center justify-center text-xs uppercase text-zinc-500 dark:text-zinc-500" aria-hidden="true">
                <span className="flex-grow h-px bg-zinc-200 dark:bg-zinc-800"></span>
                <span className="mx-2 sm:mx-3 md:mx-4 font-semibold tracking-wider">or</span>
                <span className="flex-grow h-px bg-zinc-200 dark:bg-zinc-800"></span>
              </div>

              <div className="flex justify-center">
                <motion.button
                  onClick={() => setIsCameraOpen(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-2 sm:py-3 rounded-lg md:rounded-xl text-sm sm:text-base font-semibold transition-colors bg-white/60 dark:bg-zinc-800/80 hover:bg-white/90 dark:hover:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-100 dark:focus:ring-offset-zinc-950 focus:ring-teal-500"
                >
                  <CameraIcon className="w-4 h-4 sm:w-5 sm:h-5 text-teal-500" />
                  Take Photo
                </motion.button>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="w-full max-w-5xl mt-4 sm:mt-6 md:mt-10 lg:mt-12 xl:mt-16">
              {/* Cards Removed */}
            </motion.div>
          </motion.div>
        </div>
      </UploaderBackground>
    </>
  );
};

export default ImageUploader;